package ca.northroot.app.ui.dashboard

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ca.northroot.app.data.*
import ca.northroot.app.ui.components.NorthrootCard
import ca.northroot.app.ui.theme.*
import kotlinx.coroutines.launch

import kotlinx.datetime.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DashboardScreen(
    db: Db,
    companyId: String,
    userProfile: UserProfile?,
    themeMode: ThemeMode,
    onThemeChange: (ThemeMode) -> Unit,
    onLogout: () -> Unit,
    onNavigateToJobs: () -> Unit,
    onNavigateToCustomers: () -> Unit,
    onNavigateToLiveMap: () -> Unit,
    onNavigateToPayReports: () -> Unit
) {
    val isAdmin = userProfile?.role == UserRole.ADMIN || userProfile?.role == UserRole.OWNER
    if (!isAdmin) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Text("Access Denied", style = MaterialTheme.typography.headlineMedium, color = Color.Red)
        }
        return
    }

    var pendingJobsCount by remember { mutableStateOf(0) }
    var weeklyPerformance by remember { mutableStateOf(listOf(0.4f, 0.6f, 0.8f, 0.5f, 0.9f, 0.3f, 0.7f)) }
    var showThemeDialog by remember { mutableStateOf(false) }

    LaunchedEffect(companyId) {
        if (companyId.isBlank()) return@LaunchedEffect
        db.getJobs(companyId, 0).collect { jobList ->
            pendingJobsCount = jobList.count { it.status == JobStatus.PENDING || it.status == JobStatus.IN_PROGRESS }
        }
    }

    val today = remember { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
    val dateString = "${today.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }}, ${today.month.name.lowercase().replaceFirstChar { it.uppercase() }} ${today.dayOfMonth}"

    Scaffold(
        topBar = {
            TopAppBar(
                title = { 
                    Column {
                        Text("Northroot Pro", fontWeight = FontWeight.ExtraBold, color = MaterialTheme.colorScheme.primary)
                        Text("ADMIN CONSOLE", style = MaterialTheme.typography.labelSmall, color = NorthrootGold, letterSpacing = 1.sp)
                    }
                },
                actions = {
                    IconButton(onClick = { showThemeDialog = true }) {
                        Icon(Icons.Default.Settings, "Theme", tint = MaterialTheme.colorScheme.primary)
                    }
                    IconButton(onClick = onLogout) {
                        Icon(Icons.Default.Logout, "Sign Out", tint = MaterialTheme.colorScheme.primary)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = MaterialTheme.colorScheme.surface)
            )
        },
        containerColor = MaterialTheme.colorScheme.background
    ) { padding ->
        // Force the outer container to be fillMaxSize and use verticalScroll
        Box(
            modifier = Modifier
                .padding(padding)
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(20.dp)
            ) {
                // Header Greeting
                Column {
                    Text(
                        "Good morning, Admin", 
                        style = MaterialTheme.typography.headlineSmall, 
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onBackground
                    )
                    Text(
                        dateString,
                        style = MaterialTheme.typography.bodyMedium,
                    color = NorthrootGray
                )
            }

            // Quick Stats
            StatCard(
                title = "Active Jobs",
                value = pendingJobsCount.toString(),
                icon = Icons.Default.Build,
                color = NorthrootGold,
                modifier = Modifier.fillMaxWidth()
            )

            // Performance Chart Section
                NorthrootCard {
                    Column(Modifier.padding(20.dp)) {
                        Text("Job Performance", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleMedium)
                        Text("Weekly completions vs schedule", style = MaterialTheme.typography.bodySmall, color = NorthrootGray)
                        
                        Spacer(Modifier.height(24.dp))
                        
                        // Simple Custom Chart
                        Box(Modifier.fillMaxWidth().height(120.dp), contentAlignment = Alignment.BottomStart) {
                            PerformanceMiniChart(
                                data = weeklyPerformance,
                                primaryColor = MaterialTheme.colorScheme.primary,
                                secondaryColor = NorthrootGold
                            )
                        }
                        
                        Spacer(Modifier.height(16.dp))
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                            val days = listOf("M", "T", "W", "T", "F", "S", "S")
                            days.forEach { day ->
                                Text(day, style = MaterialTheme.typography.labelSmall, color = NorthrootGray)
                            }
                        }
                    }
                }

                Text("Business Operations", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

                // Quick Action Grid
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    DashboardActionItem(
                        title = "Daily Schedule",
                        desc = "View today's crew schedule",
                        icon = Icons.Default.CalendarToday,
                        color = MaterialTheme.colorScheme.primary,
                        onClick = onNavigateToJobs
                    )
                    DashboardActionItem(
                        title = "Client Directory",
                        desc = "Search and manage customers",
                        icon = Icons.Default.People,
                        color = MaterialTheme.colorScheme.primary,
                        onClick = onNavigateToCustomers
                    )
                    DashboardActionItem(
                        title = "Live Team Map",
                        desc = "Real-time crew locations",
                        icon = Icons.Default.LocationOn,
                        color = MaterialTheme.colorScheme.primary,
                        onClick = onNavigateToLiveMap
                    )
                    DashboardActionItem(
                        title = "Pay Reports",
                        desc = "Employee hours & earnings",
                        icon = Icons.Default.Info,
                        color = NorthrootGold,
                        onClick = onNavigateToPayReports
                    )
                }
                
                Spacer(Modifier.height(40.dp))
            }
        }
    }

    if (showThemeDialog) {
        ThemeSelectionDialog(themeMode, onThemeChange) { showThemeDialog = false }
    }
}

@Composable
fun StatCard(
    title: String,
    value: String,
    icon: ImageVector,
    color: Color,
    modifier: Modifier = Modifier
) {
    NorthrootCard(modifier = modifier) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Box(
                Modifier.size(36.dp).clip(RoundedCornerShape(10.dp)).background(color.copy(alpha = 0.12f)),
                contentAlignment = Alignment.Center
            ) {
                Icon(icon, null, tint = color, modifier = Modifier.size(20.dp))
            }
            Column {
                Text(title, style = MaterialTheme.typography.labelSmall, color = NorthrootGray)
                Text(value, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Black, color = MaterialTheme.colorScheme.onSurface)
            }
        }
    }
}

@Composable
fun DashboardActionItem(
    title: String,
    desc: String,
    icon: ImageVector,
    color: Color,
    onClick: () -> Unit
) {
    NorthrootCard(onClick = onClick) {
        Row(
            Modifier.padding(16.dp).fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                Modifier.size(44.dp).clip(CircleShape).background(color.copy(alpha = 0.1f)),
                contentAlignment = Alignment.Center
            ) {
                Icon(icon, null, tint = color, modifier = Modifier.size(22.dp))
            }
            Spacer(Modifier.width(16.dp))
            Column(Modifier.weight(1f)) {
                Text(title, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyLarge)
                Text(desc, style = MaterialTheme.typography.bodySmall, color = NorthrootGray)
            }
            Icon(Icons.Default.ArrowForward, null, tint = NorthrootLightGray)
        }
    }
}

@Composable
fun PerformanceMiniChart(data: List<Float>, primaryColor: Color, secondaryColor: Color) {
    Canvas(Modifier.fillMaxSize()) {
        val width = size.width
        val height = size.height
        val barWidth = (width / data.size) * 0.6f
        val spacing = (width / data.size) * 0.4f
        
        data.forEachIndexed { index, value ->
            val left = index * (barWidth + spacing) + (spacing / 2)
            val barHeight = height * value
            
            drawRoundRect(
                color = if (index % 2 == 0) primaryColor else secondaryColor,
                topLeft = Offset(left, height - barHeight),
                size = Size(barWidth, barHeight),
                cornerRadius = CornerRadius(4.dp.toPx())
            )
        }
    }
}
